/**
 * useGridA11y
 *
 * Composable that augments a Kendo-based grid with accessible keyboard navigation,
 * focus management, and custom behaviors for column header menus (filter/sort).
 */

import { ref, nextTick, onBeforeUnmount, type Ref } from "vue";
import type {
  SortDescriptor,
  GridColumnProps,
  GridSortChangeEvent,
} from "../types/kendo";

export const useGridA11y = (
  gridRef: Ref<any>,
  options?: { focusableMenuSelectors?: string[] },
) => {
  // Reference to the active filter button for focus management
  const activeFilterButton = ref<HTMLElement | null>(null);

  let a11yInitialized = false;

  // Mutation observer for dynamic elements
  let mutationsObserver: MutationObserver | null = null;

  // Observer to watch row changes within this grid instance so we can reassert tabindex
  let rowObserver: MutationObserver | null = null;

  const headerThTeardownCallbacks: Array<() => void> = [];

  const intraRowNavigationSelector =
    ".k-table-row[data-grid-row-index] [tabindex]";

  const getGridRootElement = (): HTMLElement | null => {
    const host = gridRef?.value?.$el as HTMLElement | undefined;
    if (!host) return null;
    return host.closest(".k-grid") as HTMLElement | null;
  };

  const isWithinThisGridScope = (target: HTMLElement): boolean => {
    const gridRoot = getGridRootElement();
    if (!gridRoot) return false;

    // Preferred scope: Kendo's grid accessibility region.
    const ariaRoot = target.closest(".k-grid-aria-root") as HTMLElement | null;
    if (ariaRoot) {
      return gridRoot.contains(ariaRoot);
    }

    // Fallback for markup/theme variance across versions.
    const fallbackScope = target.closest(
      ".k-grid-header, .k-grid-content, .k-grid-content-locked",
    ) as HTMLElement | null;
    return fallbackScope ? gridRoot.contains(fallbackScope) : false;
  };

  const isEnterActivation = (event: KeyboardEvent) => {
    return (
      event.key === "Enter" ||
      event.code === "Enter" ||
      event.code === "NumpadEnter"
    );
  };

  // Helper to get columns from gridRef
  const getColumns = () => {
    return (gridRef?.value as any)?.columns as Array<GridColumnProps>;
  };

  /**
   * Key activation handler for a header's column menu / filter button.
   *
   * Purpose:
   *  - Enables keyboard users (Space / Enter) to open the Kendo column menu without
   *    triggering unintended default behaviors (like sorting) that Kendo may bind
   *    to key events on header elements.
   *
   * Behavior:
   *  - Intercepts Space / Enter (including legacy "Spacebar") keys.
   *  - Prevents default and stops propagation to suppress native scrolling or
   *    upstream sort handlers.
   *  - Records the event target in `activeFilterButton` so focus can be restored
   *    after the menu closes (Escape handling in other logic).
   *  - Programmatically clicks the target to open the column menu.
   *
   * Accessibility:
   *  - Treats the underlying element as a button activation; complements ARIA
   *    role="button" applied elsewhere.
   *
   * Idempotency / Safety:
   *  - Safe to attach to multiple buttons; only responds to specified keys.
   *
   * @param event KeyboardEvent originating on the column menu trigger.
   * @returns void
   */
  const handleFilterButtonKeyboardListener = (event: KeyboardEvent) => {
    const key = event.key || event.code;
    if ([" ", "Spacebar", "Space"].includes(key) || isEnterActivation(event)) {
      // Prevents the unintended default sorting on keyboard event
      event.preventDefault();
      event.stopPropagation();

      // Store reference to the filter button when it is activated
      activeFilterButton.value = event.target as HTMLElement;

      // Trigger the click event on the filter button
      (event.target as HTMLElement).click();
    }
  };

  /**
   * Keyboard interaction handler for an open Kendo Grid column menu (filter / sort menu).
   *
   * Scope:
   *  - Attached to each popup (".k-animation-container") when it appears.
   *  - Handles navigation inside two possible UI states:
   *      1. Column menu list (".k-columnmenu-item") items.
   *      2. Filter form container (".k-filter-menu-container") with inputs/buttons.
   *
   * Keys:
   *  - Escape:
   *      * Closes semantic focus context by returning focus to `activeFilterButton`
   *        (the header or trigger element that opened the menu) and exits.
   *  - ArrowUp / ArrowDown (only when NOT in a filter form container):
   *      * Cycles focus among menu items (wraps at ends).
   *  - Tab within filter form container:
   *      * Traps focus inside the filter form’s focusable elements; Shift+Tab reverses.
   *  - Tab when not in a filter form container:
   *      * Cycles focus among sibling trigger buttons adjacent to the originating header
   *        (previousElementSibling / nextElementSibling of the active trigger).
   *
   * Focus Model:
   *  - Relies on `activeFilterButton` (external ref) to restore focus after Escape or
   *    to traverse sibling triggers on plain Tab outside a filter form.
   *
   * Guard Clauses:
   *  - Returns immediately if no `activeFilterButton` is set (defensive).
   *
   * Assumptions:
   *  - Menu markup follows Kendo’s structure with ".k-animation-container" wrapper.
   *  - Filter form presence is detectable via ".k-filter-menu-container".
   *  - Menu action items carry class ".k-columnmenu-item".
   *
   * Side Effects:
   *  - Calls focus() on controlled elements.
   *  - Prevents default + stops propagation for handled keys to avoid native browser
   *    tab order / scroll interference or Kendo’s default key handling.
   *
   * @param event KeyboardEvent dispatched on the popup container.
   * @returns void
   */
  const handleFilterMenuKeyboardListener = (event: KeyboardEvent) => {
    if (!activeFilterButton.value) return;
    if (event.code === "Escape") {
      event.stopPropagation();

      if (activeFilterButton.value) {
        activeFilterButton.value.focus();
      }
      return;
    }

    const menuItems = Array.from(
      document.querySelectorAll(
        ".k-animation-container .k-popup .k-column-menu .k-columnmenu-item-wrapper .k-columnmenu-item",
      ),
    ) as HTMLElement[];

    const filterContainer = document.querySelector(
      ".k-animation-container .k-popup .k-column-menu .k-filter-menu-container",
    );

    if (filterContainer) {
      if (event.code === "Tab") {
        const defaultFocusable = [
          // ".k-filter-menu-container .k-dropdownlist[tabindex='0']",
          // ".k-filter-menu-container input.k-input-inner:not([tabindex='-1']):not([disabled])",
          // ".k-filter-menu-container button:not([tabindex='-1']):not([disabled])",
          // ".k-checkbox",
          ".k-dropdownlist[tabindex='0']",
          "input.k-input-inner:not([tabindex='-1']):not([disabled])",
          "button:not([tabindex='-1']):not([disabled])",
          ".k-checkbox",
        ];
        const focusableSelectors =
          options?.focusableMenuSelectors ?? defaultFocusable;
        const focusableElements = Array.from(
          filterContainer.querySelectorAll(focusableSelectors.join(",")),
        ) as HTMLElement[];

        if (focusableElements.length === 0) return;

        const focusedIndex = focusableElements.findIndex(
          (el) => el === document.activeElement,
        );
        let targetIndex: number;
        if (focusedIndex === -1) {
          targetIndex = 0;
        } else if (event.shiftKey) {
          targetIndex =
            (focusedIndex - 1 + focusableElements.length) %
            focusableElements.length;
        } else {
          targetIndex = (focusedIndex + 1) % focusableElements.length;
        }

        event.preventDefault();
        event.stopPropagation();

        focusableElements[targetIndex]?.focus();
      }
    } else {
      if (event.code === "ArrowUp" || event.code === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();

        const focusedIndex = menuItems.findIndex(
          (item) => item === document.activeElement,
        );

        let targetIndex = focusedIndex;
        if (event.code === "ArrowUp") {
          targetIndex =
            focusedIndex > 0 ? focusedIndex - 1 : menuItems.length - 1;
        } else if (event.code === "ArrowDown") {
          targetIndex =
            focusedIndex < menuItems.length - 1 ? focusedIndex + 1 : 0;
        }

        menuItems[targetIndex]?.focus();
        return;
      }
    }

    if (event.code === "Tab") {
      event.preventDefault();
      event.stopPropagation();

      const currentTh = activeFilterButton.value?.parentElement;
      const targetTh = event.shiftKey
        ? currentTh?.previousElementSibling
        : currentTh?.nextElementSibling;
      const targetButton = targetTh?.querySelector(
        "button, [role='button'], .k-link",
      ) as HTMLElement;

      targetButton?.focus();
    }
  };

  /**
   * Observes the document body for Kendo Grid column menu (filter/sort menu) lifecycle.
   *
   * Responsibilities:
   *  - Creates a MutationObserver watching document.body (childList + subtree) for nodes
   *    with class "k-animation-container" (the popup wrapper Kendo inserts/ removes).
   *  - For each added animation container:
   *      * If the active header is NOT sortable (data-feather-k-sortable="false"), removes
   *        any menu wrapper whose text content includes "sort" (case-insensitive) to hide
   *        irrelevant sort options from the popup UI.
   *      * Attaches a keydown listener (handleFilterMenuKeyboardListener) to enable:
   *          - ArrowUp / ArrowDown navigation through menu items
   *          - Tab trapping / cycling in filter form content
   *          - Escape to return focus to the triggering header/filter button
   *      * Uses a retry loop (nextTick + setTimeout) to auto-focus & auto-activate a lone
   *        menu item (common when only "Filter" is present) improving keyboard efficiency.
   *  - For each removed animation container (and any nested ones), detaches the keydown listener.
   *
   * Behavior Notes:
   *  - Sort option pruning only occurs when the triggering column is not sortable. The sortable
   *    state is inferred from data attribute: data-feather-k-sortable === "true".
   *  - Active trigger element is read from activeFilterButton ref.
   *  - Focus restoration after Escape is handled inside handleFilterMenuKeyboardListener.
   *
   * Idempotency:
   *  - Calling multiple times replaces the prior MutationObserver reference (without an
   *    explicit disconnect first). Current usage calls it once during initA11y(). If future
   *    calls are needed, consider disconnecting mutationsObserver beforehand to avoid
   *    duplicate observers.
   *
   * Cleanup:
   *  - Observer and event listeners are disconnected/removed by cleanupFilterButtonAccessibility().
   *
   * Side Effects:
   *  - Mutates DOM (removal of unwanted sort menu items).
   *  - Registers global (document-level subtree) MutationObserver.
   *  - Adds / removes keydown event listeners on popup containers.
   *
   * Preconditions:
   *  - document.body is available (browser environment).
   *  - activeFilterButton may reference the header/filter button that opened the menu.
   *
   * @returns void
   */
  const setupFilterMenuObserver = () => {
    mutationsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.classList.contains("k-animation-container")) {
              const column = activeFilterButton.value as HTMLElement;
              if (column) {
                const sortable = column.dataset.featherKSortable === "true";
                if (!sortable) {
                  nextTick(() => {
                    const wrappers = element.querySelectorAll(
                      ".k-columnmenu-item-wrapper",
                    );
                    wrappers.forEach((wrapper) => {
                      const sortMenu = wrapper.textContent
                        ?.toLowerCase()
                        .includes("sort");
                      if (sortMenu) {
                        wrapper.remove();
                      }
                    });
                  });
                }
              }
              element.addEventListener(
                "keydown",
                handleFilterMenuKeyboardListener,
              );
              nextTick(() => {
                const tryFocusMenuItem = () => {
                  const menuItems = Array.from(
                    element.querySelectorAll(
                      ".k-animation-container .k-popup .k-column-menu .k-columnmenu-item-wrapper .k-columnmenu-item",
                    ),
                  ) as HTMLElement[];

                  if (menuItems.length === 1) {
                    menuItems[0].focus();
                    menuItems[0].click();
                    (tryFocusMenuItem as any).attempts = 0; // reset attempts
                  } else if (menuItems.length > 1) {
                    (tryFocusMenuItem as any).attempts = 0; // reset attempts
                    return;
                  } else {
                    if ((tryFocusMenuItem as any).attempts === undefined)
                      (tryFocusMenuItem as any).attempts = 0;
                    if ((tryFocusMenuItem as any).attempts++ < 3) {
                      setTimeout(tryFocusMenuItem, 200);
                    }
                  }
                };
                tryFocusMenuItem();
              });
            }

            const animationContainers = element.querySelectorAll(
              ".k-animation-container",
            );
            animationContainers.forEach((container) => {
              (container as HTMLElement).addEventListener(
                "keydown",
                handleFilterMenuKeyboardListener,
              );
            });
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            if (element.classList.contains("k-animation-container")) {
              element.removeEventListener(
                "keydown",
                handleFilterMenuKeyboardListener,
              );
            }

            const animationContainers = element.querySelectorAll(
              ".k-animation-container",
            );
            animationContainers.forEach((container) => {
              (container as HTMLElement).removeEventListener(
                "keydown",
                handleFilterMenuKeyboardListener,
              );
            });
          }
        });
      });
    });

    mutationsObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  /**
   * Detects if an element is a form control or inside a form-like component.
   *
   * Purpose:
   *  - Allows grid keyboard navigation to skip form controls, letting them handle
   *    their own keyboard events naturally (e.g., cursor movement in inputs, spinners).
   *
   * Behavior:
   *  - Returns true if the element itself is an input, textarea, select, or button.
   *  - Returns true if the element is inside a Kendo form component:
   *      * .k-picker-wrap (DatePicker, TimePicker, etc.)
   *      * .k-input-inner (MaskedTextBox, TextBox, etc.)
   *      * .k-textbox (generic Kendo textbox wrapper)
   *      * [role="combobox"] (dropdown, autocomplete, etc.)
   *  - Returns false otherwise.
   *
   * @param el HTMLElement to check
   * @returns true if el is a form control that should manage its own keyboard events
   */
  const isFormControl = (el: HTMLElement): boolean => {
    const tagName = el.tagName.toLowerCase();

    // Check if element itself is a native form control
    if (["input", "textarea", "select", "button"].includes(tagName)) {
      return true;
    }

    // Check if element is inside a Kendo form component wrapper
    if (
      el.closest(
        '.k-picker-wrap, .k-input-inner, .k-textbox, [role="combobox"]',
      )
    ) {
      return true;
    }

    return false;
  };

  /**
   * Keyboard handler for the Grid root element (bind to @keydown on <Grid>).
   *
   * Responsibilities:
   *  - ArrowUp / ArrowDown: Move focus (and tabindex="0") between sibling data rows
   *    (.k-table-row[data-grid-row-index]).
   *  - ArrowLeft / ArrowRight: When focus is on (or within) a data row, cycle focus
   *    among tabbable descendants inside that row (as defined by
   *    `intraRowNavigationSelector`). If the row itself has focus and no descendant
   *    is focused, ArrowRight focuses the first intra-row tabbable element.
   *  - Escape: If focus is currently inside a data row descendant, collapse focus
   *    back to the parent row element (ensuring it has tabindex="0").
   *  - Enter / Space: Reserved for future enhancements and prevented when part of
   *    the core navigation key group to avoid unintended native behaviors.
   *
   * Special Cases:
   *  - If the event target is a grid column menu button (identified by both
   *    "k-grid-header-menu" and "k-grid-column-menu" classes), the handler stores
   *    it in `activeFilterButton` and returns so menu logic can proceed elsewhere.
   *  - If the event target is a form control (input, button, Kendo component, etc.),
   *    the handler exits early to allow the form control to manage its own keyboard
   *    events naturally.
   *
   * Side Effects:
   *  - Mutates tabindex attributes on row elements to maintain exactly one row
   *    with tabindex="0" in the vertical navigation path.
   *  - Calls focus() on target rows or intra-row elements as navigation occurs.
   *
   * Defensive Checks:
   *  - Exits early if the event object, its type, or target is missing.
   *  - Exits early if target is a form control (via isFormControl).
   *
   * Assumptions:
   *  - `gridRef.value.$el` is the Grid root (or inside it) and queryable.
   *  - Data rows use the selector ".k-table-row[data-grid-row-index]".
   *
   * @param e KeyboardEvent from the Grid's @keydown binding.
   * @returns void
   */
  const handleGridKeyDown = (e: KeyboardEvent) => {
    if (!e.type) return;

    if (!e) {
      return;
    }
    const target = e.target as HTMLElement;
    if (!target) return;

    const row = target.closest(
      ".k-table-row[data-grid-row-index]",
    ) as HTMLElement | null;

    // Scope keyboard handling to this grid instance's a11y/content region.
    // This prevents toolbar and external controls from being intercepted.
    if (!isWithinThisGridScope(target)) {
      return;
    }

    // EXIT EARLY: Don't apply grid navigation to form controls.
    // Exception: allow ArrowLeft/ArrowRight intra-row navigation for focusable
    // controls that live inside a data row.
    const isIntraRowHorizontalNavKey = ["ArrowLeft", "ArrowRight"].includes(
      e.code,
    );
    if (isFormControl(target) && !(row && isIntraRowHorizontalNavKey)) {
      return;
    }

    // If Escape pressed and the active element is inside a data row,
    // move focus to the parent .k-table-row and ensure its tabindex=0.
    if (e.code === "Escape") {
      const focusedRow = (
        document.activeElement as HTMLElement | null
      )?.closest(".k-table-row[data-grid-row-index]") as HTMLElement | null;
      if (focusedRow) {
        // Justified: We're handling Escape to collapse focus from descendant to row.
        // Prevent default to override native browser behavior (dialog close, etc.)
        e.preventDefault();
        // Justified: Stop propagation to prevent parent containers from seeing this Escape.
        e.stopPropagation();
        try {
          const rows = Array.from(
            gridRef?.value.$el.querySelectorAll(
              ".k-table-row[data-grid-row-index]",
            ) as HTMLElement[],
          );
          rows.forEach((r) => r.setAttribute("tabindex", "-1"));
        } catch {
          // ignore if gridRef not available
        }
        focusedRow.setAttribute("tabindex", "0");
        focusedRow.focus();
        return;
      }
    }

    // Handle navigation keys (only prevent/stop for keys we actually handle)
    if (
      [
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "Enter",
        "NumpadEnter",
        "Space",
      ].includes(e.code)
    ) {
      // Check if target is a grid column menu button
      if (
        target.classList.contains("k-grid-header-menu") &&
        target.classList.contains("k-grid-column-menu")
      ) {
        activeFilterButton.value = target as HTMLElement;
        // Let menu logic proceed elsewhere; don't prevent or stop
        return;
      }

      // If event came from within a data row, handle row/intra-row navigation
      if (row) {
        if (["ArrowDown", "ArrowUp"].includes(e.code)) {
          // Justified: We're handling ArrowUp/Down for vertical row navigation.
          // Prevent default to override native browser scrolling behavior.
          e.preventDefault();
          // Justified: Stop propagation to prevent parent containers from seeing this.
          e.stopPropagation();

          // Find the next/previous element sibling that actually has a tabindex.
          const findSiblingRow = (
            start: Element,
            direction: "next" | "previous",
          ): HTMLElement | null => {
            let sib =
              direction === "next"
                ? start.nextElementSibling
                : start.previousElementSibling;
            while (sib) {
              const el = sib as HTMLElement;
              try {
                if (el.classList.contains("k-table-row")) {
                  return el;
                }
              } catch {
                // Defensive: some nodes might throw when accessed; skip them.
              }
              sib =
                direction === "next"
                  ? sib.nextElementSibling
                  : sib.previousElementSibling;
            }
            return null;
          };

          const targetRow =
            e.code === "ArrowDown"
              ? findSiblingRow(row, "next")
              : findSiblingRow(row, "previous");

          if (targetRow) {
            row.setAttribute("tabindex", "-1");
            targetRow.setAttribute("tabindex", "0");
            targetRow.focus();
          }
          return;
        }
        if (["ArrowLeft", "ArrowRight"].includes(e.code)) {
          // Justified: We're handling ArrowLeft/Right for intra-row focus navigation.
          // Prevent default to override native text cursor movement only when we handle the event.
          e.preventDefault();
          // Justified: Stop propagation to prevent parent containers from seeing this.
          e.stopPropagation();

          const tabbable = row.querySelectorAll(
            intraRowNavigationSelector,
          ) as NodeListOf<HTMLElement>;
          if (tabbable.length === 0) return;

          // Find currently focused element within the row
          let currentIndex = Array.from(tabbable).findIndex(
            (el) => el === document.activeElement,
          );

          if (currentIndex === -1 && document.activeElement === row) {
            // If no element is focused, focus the first tabbable element
            (tabbable[0] as HTMLElement).focus();
            return;
          }

          if (e.code === "ArrowRight") {
            currentIndex =
              currentIndex === tabbable.length - 1 ? 0 : currentIndex + 1;
          } else if (e.code === "ArrowLeft") {
            currentIndex =
              currentIndex === 0 ? tabbable.length - 1 : currentIndex - 1;
          }
          (tabbable[currentIndex] as HTMLElement).focus();

          return;
        }
      }

      // Enter, Space: Not currently handled for grid navigation.
      // Don't prevent by default to allow native behavior or other handlers to respond.
    }
  };

  /**
   * Applies a FeatherK-specific CSS hook class to the Kendo Grid root element.
   *
   * Behavior:
   *  - On the next Vue microtask (nextTick), locates the closest ancestor with the
   *    ".k-grid" class starting from `gridRef.value.$el`.
   *  - Adds the "fk-grid" class to that element (no-op if already present).
   *
   * Purpose:
   *  - Provides a styling hook so external FeatherK stylesheets can target grids
   *    that have been enhanced by this composable without relying on brittle
   *    selectors or global overrides.
   *
   * Safety / Idempotency:
   *  - Safe to call multiple times; subsequent calls simply re-add the same class.
   *  - Silently does nothing if `gridRef.value` or its `$el` is not yet available.
   *
   * Side Effects:
   *  - Mutates the DOM by adding a CSS class to the grid root element.
   *
   * No cleanup is currently required (class need not be removed on unmount).
   *
   * @returns void
   */
  const setupGridStyling = () => {
    nextTick(() => {
      const gridHost = gridRef.value?.$el as Element | undefined;
      if (!gridHost || typeof gridHost.closest !== "function") return;

      const gridElement = gridHost.closest(".k-grid") as HTMLElement | null;
      if (gridElement) gridElement.classList.add("fk-grid");
    });
  };

  /**
   * Sets up row-level (tr) keyboard navigation for the Kendo Grid instance.
   *
   * Behavior:
   * 1. Ensures exactly one data row ('.k-table-row[data-grid-row-index]') has tabindex="0"
   *    (the current keyboard focus target) and all others have tabindex="-1".
   * 2. Establishes an internal helper (ensureSingleTabindex) that:
   *    - Re-calculates which row should retain tabindex="0" after DOM changes.
   *    - Preserves a focused row if its descendants currently contain focus.
   *    - Falls back to the first row when no focused row exists.
   * 3. Applies initial tabindex values immediately (first row gets focusable state).
   * 4. Observes the grid <tbody> ('.k-table-tbody') with a MutationObserver so that when
   *    Kendo re-renders rows (sorting, filtering, paging, data updates), tabindex
   *    constraints are re-applied with minimal work.
   *
   * Rationale:
   * The default Kendo Grid may introduce multiple tabbable descendants, making
   * keyboard traversal verbose. Restricting tab stops to a single row improves
   * accessibility by:
   *  - Reducing unnecessary Tab presses.
   *  - Enabling arrow-key driven intra-row navigation (handled elsewhere).
   *
   * Assumptions / Dependencies:
   *  - gridRef.value.$el points to (or is inside) a rendered Kendo Grid root.
   *  - Data rows carry class 'k-table-row' and attribute 'data-grid-row-index'.
   *  - Runs in a browser environment (document, MutationObserver available).
   *
   * Error Handling:
   *  - Wrapped in try/catch; failures are logged and do not throw upstream.
   *
   * Idempotency:
   *  - Safe to call more than once; repeated calls will attach at most one observer
   *    per invocation lifecycle (previous observer persists). Caller is expected
   *    to avoid redundant calls or add future cleanup if needed.
   *
   * Side Effects:
   *  - Mutates DOM attributes (tabindex) of grid row elements.
   *  - Creates a MutationObserver stored in the closure (rowObserver) for teardown
   *    via cleanupFilterButtonAccessibility().
   *
   * @returns void
   */
  const setupTrNavigation = () => {
    if (rowObserver) {
      // Already set up
      return;
    }
    try {
      /**
       * Ensure exactly one grid row is keyboard-focusable (tabindex="0") and make all others unfocusable (tabindex="-1").
       *
       * This function:
       * - Queries the grid DOM for rows using the selector ".k-table-row[data-grid-row-index]".
       * - If no rows are found, it returns immediately.
       * - If exactly one row already has tabindex="0", it does nothing.
       * - Otherwise it clears tabindex to "-1" on all matched rows, then:
       *   - If the currently focused element is inside one of the matched rows, that row is preserved as tabindex="0".
       *   - If there is no such focused row, the first matched row is set to tabindex="0".
       *
       * Notes:
       * - The function mutates DOM attributes (tabindex) of the matched row elements.
       * - It expects to run in a browser environment where `document.activeElement` is available and where
       *   `gridRef.value.$el` points to the grid root element containing rows matching the selector.
       * - Any runtime errors encountered while querying or updating the DOM are caught and logged via console.error;
       *   the function will not throw.
       *
       * @remarks
       * Use this helper to maintain a single tabbable row for keyboard navigation / accessibility purposes within a custom grid component.
       *
       * @returns void
       *
       * @example
       * // Keep one tabbable row in the grid after rendering or when rows change
       * ensureSingleTabindex();
       */
      const ensureSingleTabindex = () => {
        try {
          const rows = Array.from(
            gridRef.value.$el.querySelectorAll(
              ".k-table-row[data-grid-row-index]",
            ) as HTMLElement[],
          );

          if (!rows || rows.length === 0) return;

          // Count rows that currently have tabindex="0"
          const zeroTabRows = rows.filter(
            (r) => r.getAttribute("tabindex") === "0",
          );

          // If exactly one row already has tabindex=0, nothing to do
          if (zeroTabRows.length === 1) return;

          // Prefer to preserve the currently focused row if it's inside the rows
          const focusedRow = rows.find(
            (r) =>
              r === document.activeElement ||
              r.contains(document.activeElement),
          );

          // Clear all to -1
          rows.forEach((r) => r.setAttribute("tabindex", "-1"));

          if (focusedRow) {
            focusedRow.setAttribute("tabindex", "0");
            return;
          }

          // Fallback: set first row to tabindex=0
          rows[0].setAttribute("tabindex", "0");
        } catch (err) {
          console.error("ensureSingleTabindex error:", err);
        }
      };

      // Initial setup
      const rows = Array.from(
        gridRef.value.$el.querySelectorAll(
          ".k-table-row[data-grid-row-index]",
        ) as HTMLElement[],
      );
      if (rows.length > 0) {
        rows.forEach((row, index) => {
          row.setAttribute("tabindex", index === 0 ? "0" : "-1");
        });
      }

      // Observe tbody for row changes (Kendo will recreate rows on sort/filter/paging)
      const tbody = gridRef.value.$el.querySelector(".k-table-tbody");
      if (tbody) {
        rowObserver = new MutationObserver(() => {
          // Reassert single tabindex after DOM changes. Keep work minimal.
          ensureSingleTabindex();
        });
        rowObserver.observe(tbody, { childList: true, subtree: true });
      }
    } catch (error) {
      console.error("Error setting up row navigation:", error);
    }
  };

  /**
   * Initializes accessibility behavior for grid filter buttons.
   *
   * - Queries the DOM for elements matching the selector
   *   ".k-grid-header .k-grid-header-menu.k-grid-column-menu".
   * - For each matched element, sets the ARIA role to "button" and attaches a
   *   keydown event listener using handleFilterButtonKeyboardListener.
   * - Invokes setupFilterMenuObserver() to observe changes to filter menus.
   * - Invokes setupHeaderThTriggers() to initialize any header-related triggers.
   *
   * @returns void
   */
  const setupFilterButtonAccessibility = () => {
    nextTick(() => {
      const filterSelector =
        ".k-grid-header .k-grid-header-menu.k-grid-column-menu";
      const filterButtons = document.querySelectorAll(filterSelector);
      if (filterButtons) {
        filterButtons.forEach((el) => {
          el.setAttribute("role", "button");
          (el as HTMLElement).addEventListener(
            "keydown",
            handleFilterButtonKeyboardListener,
          );
        });
      }

      setupFilterMenuObserver();

      setupHeaderThTriggers();
    });
  };

  /**
   * Cleans up all accessibility (a11y) resources installed by useGridA11y.
   *
   * Includes:
   *  - Removes keydown listeners from column menu / filter buttons.
   *  - Disconnects the column menu MutationObserver.
   *  - Disconnects the row MutationObserver.
   *  - Executes and clears header <th> teardown callbacks.
   *
   * Idempotent: safe to call multiple times.
   */
  const cleanupGridA11y = () => {
    const filterSelector =
      ".k-grid-header .k-grid-header-menu.k-grid-column-menu";
    const filterButtons = document.querySelectorAll(filterSelector);
    if (filterButtons) {
      filterButtons.forEach((el) => {
        (el as HTMLElement).removeEventListener(
          "keydown",
          handleFilterButtonKeyboardListener,
        );
      });
    }

    if (mutationsObserver) {
      mutationsObserver.disconnect();
      mutationsObserver = null;
    }

    if (rowObserver) {
      rowObserver.disconnect();
      rowObserver = null;
    }

    headerThTeardownCallbacks.forEach((teardown) => teardown());
    headerThTeardownCallbacks.length = 0;
  };

  /**
   * Initializes accessible interaction handlers for header <th> cells in a Kendo-style grid.
   *
   * Finds all header cells under ".k-grid-header .k-table-thead th" and for each header cell:
   * - Locates the column menu button (".k-grid-header-menu.k-grid-column-menu") and sets its tabindex to -1.
   * - Reads column metadata from getColumns() (external dependency) to set data attributes:
   *   - data-feather-k-field (column.field)
   *   - data-feather-k-filterable ("false" if column.filterable === false, otherwise "true")
   *   - data-feather-k-sortable ("false" if column.sortable === false, otherwise "true")
   * - Determines whether the header is filterable or sortable using the above data attributes.
   * - Sets appropriate cursor styles (pointer for interactive headers, default otherwise).
   * - Configures ARIA/sniffable roles:
   *   - If filterable: tabindex="0", role="button", aria-haspopup="menu"
   *   - If not filterable: tabindex="0", role="columnheader", aria-haspopup is removed
   *
   * Adds event handlers for each header cell:
   * - click handler:
   *   - If filterable: prevents default/propagation and opens the column menu (via the menu button click). Also records the header element on activeFilterButton (external ref) for focus return.
   *   - If not filterable but sortable: synthesizes an "Enter" KeyboardEvent and dispatches it on the header to trigger sort behavior and records activeFilterButton.
   *   - Clicks originating from a column resizer (closest ".k-column-resizer") are ignored.
   * - keydown handler (registered in capture phase for the header cell):
   *   - On Enter or Space when filterable or sortable:
   *     - Sets activeFilterButton to the header and focuses it.
   *     - If filterable: prevents default/propagation and opens the column menu.
   *     - If sortable: clicks the first ".k-link" inside the header to trigger sort.
   *
   * Additionally, attaches a capturing keydown handler to the enclosing THEAD element (".k-grid-header .k-table-thead"):
   * - Prevents default/immediate propagation for Enter/Space on headers that are neither filterable nor sortable.
   * - This protects non-interactive header cells from activating default keyboard behavior.
   *
   * All event listeners added by this setup are recorded as teardown callbacks into the
   * headerThTeardownCallbacks array (external), so they can be removed later to avoid leaks.
   *
   * Notes / assumptions:
   * - Relies on globals/closures: document, getColumns(), activeFilterButton (ref-like object), and headerThTeardownCallbacks (array).
   * - Intended to enhance accessibility and keyboard interaction for grid headers by mapping Kendo column behaviors
   *   to appropriate ARIA roles, keyboard handling, and focus management.
   *
   * @remarks
   * - Does not return a value; side effects are applied directly to DOM and external state.
   * - Uses attribute-based decisions to maintain compatibility with dynamically-updated column metadata.
   *
   * @returns void
   */
  const setupHeaderThTriggers = () => {
    const headerCells = document.querySelectorAll(
      ".k-grid-header .k-table-thead th",
    );

    headerCells.forEach((th, index) => {
      if ((th as HTMLElement).dataset.featherKHeaderBound === "true") {
        return;
      }

      const btn = th.querySelector(
        ".k-grid-header-menu.k-grid-column-menu",
      ) as HTMLElement | null;
      if (!btn) return;

      const columns = getColumns();

      if (columns && columns[index]) {
        const field = columns[index].field ?? "";
        th.setAttribute("data-feather-k-field", field);
        th.setAttribute(
          "data-feather-k-filterable",
          columns[index].filterable === false ? "false" : "true",
        );
        th.setAttribute(
          "data-feather-k-sortable",
          columns[index].sortable === false ? "false" : "true",
        );
      }

      const filterable =
        (th as HTMLElement).dataset.featherKFilterable !== "false";
      const sortable = (th as HTMLElement).dataset.featherKSortable !== "false";

      btn.setAttribute("tabindex", "-1");

      if (filterable || sortable) {
        (th as HTMLElement).style.cursor = "pointer";
      } else {
        (th as HTMLElement).style.cursor = "default";
      }
      if (filterable) {
        th.setAttribute("tabindex", "0");
        th.setAttribute("role", "button");
        th.setAttribute("aria-haspopup", "menu");
      } else {
        th.setAttribute("tabindex", "0");
        th.setAttribute("role", "columnheader");
        th.removeAttribute("aria-haspopup");
      }
      const openMenu = (e: Event | KeyboardEvent) => {
        if ((e.target as HTMLElement)?.closest(".k-column-resizer")) return;

        activeFilterButton.value = th as HTMLElement; // focus return target
        btn.click();
      };

      const clickHandler = (e: Event) => {
        if (filterable) {
          activeFilterButton.value = th as HTMLElement;
          e.preventDefault();
          e.stopPropagation();
          openMenu(e);
        } else if (sortable) {
          activeFilterButton.value = th as HTMLElement;
          const columnLink = th.querySelector(".k-link");
          if (columnLink) {
            (columnLink as HTMLElement).click();
          }
        }
      };
      (th as HTMLElement).addEventListener("click", clickHandler);
      headerThTeardownCallbacks.push(() => {
        (th as HTMLElement).removeEventListener("click", clickHandler);
      });

      // Keyboard activation for sortable/filterable header cells.
      //
      // Enter, Numpad Enter, and Space are intentionally handled identically here:
      //  - preventDefault + stopPropagation are applied for ALL three keys so
      //    none of them leak to native browser or Kendo's own key handlers,
      //    which could trigger a duplicate or conflicting sort/scroll action.
      //  - For sortable-only headers the sort is triggered by clicking the
      //    .k-link element directly (the same path the browser follows on a
      //    real mouse click and what Space was already using). This keeps
      //    the asc → desc → unsorted cycle consistent regardless of key used.
      //
      // IMPORTANT: Do not restore the old synthetic KeyboardEvent dispatch
      // (th.dispatchEvent(new KeyboardEvent('keydown', { key:'Enter', ... }))).
      // That approach caused Enter/Numpad Enter to re-enter this handler
      // recursively and be silently swallowed by the isSorting guard in
      // ClaimLog.vue, making every press after the first appear to do nothing.
      const keyHandler = (e: KeyboardEvent) => {
        if (
          (isEnterActivation(e) || e.code === "Space") &&
          (filterable || sortable)
        ) {
          e.preventDefault();
          e.stopPropagation();

          activeFilterButton.value = th as HTMLElement;
          activeFilterButton.value.focus();
          if (filterable) {
            openMenu(e);
          } else if (sortable) {
            const columnLink = th.querySelector(".k-link");
            if (columnLink) {
              (columnLink as HTMLElement).click();
            }
          }
        }
      };
      (th as HTMLElement).addEventListener("keydown", keyHandler, true);
      headerThTeardownCallbacks.push(() => {
        (th as HTMLElement).removeEventListener("keydown", keyHandler, true);
      });

      (th as HTMLElement).dataset.featherKHeaderBound = "true";
    });

    const thead = document.querySelector(".k-grid-header .k-table-thead");
    if (
      thead &&
      (thead as HTMLElement).dataset.featherKHeaderBound !== "true"
    ) {
      const theadKeyboardHandler = (e: KeyboardEvent) => {
        const th = (e.target as HTMLElement).closest("th");
        if (!th) return;
        if (
          (isEnterActivation(e) || e.code === "Space") &&
          th.dataset.featherKFilterable === "false" &&
          th.dataset.featherKSortable === "false"
        ) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      };
      (thead as HTMLElement).addEventListener(
        "keydown",
        theadKeyboardHandler,
        true, // NOTE: capture phase
      );
      headerThTeardownCallbacks.push(() => {
        (thead as HTMLElement).removeEventListener(
          "keydown",
          theadKeyboardHandler,
          true,
        );
      });

      (thead as HTMLElement).dataset.featherKHeaderBound = "true";
    }
  };

  /**
   * Handles grid sort change events with special-casing for column menu interactions and non-sortable columns.
   *
   * This function inspects the DOM target that triggered the sort change and the grid column metadata to
   * determine whether the default sort handling should proceed or be suppressed. It:
   * - Ignores events coming from the column menu toggle (element with class "k-link").
   * - When a column menu item (element with class "k-columnmenu-item") is selected for a non-sortable field,
   *   prevents modifying the sort descriptors for that field.
   * - Otherwise, if a custom handler is provided, schedules focusing of the active filter button (if present)
   *   on the next tick and then invokes the custom handler with the original event.
   *
   * @param event - Optional GridSortChangeEvent. The implementation expects a nested structure where
   *                the originating HTMLElement is available at `event.event.event.target`, the field at
   *                `event.event.field`, and the current sort descriptors at `event.event.sort`.
   * @param customHandler - Optional callback to be executed when the standard handling path is allowed.
   *                        The callback will be invoked inside `nextTick` after attempting to focus the
   *                        active filter button.
   * @returns void
   *
   * @remarks
   * - This function relies on `getColumns()` to obtain column metadata and on an `activeFilterButton` ref
   *   to optionally restore focus. It performs early returns when the event target or columns are unavailable,
   *   or when the column menu toggle was the source of the event.
   */
  const handleSortChange = function (
    event?: GridSortChangeEvent,
    customHandler?: any,
  ) {
    const target = event?.event?.event.target as HTMLElement;
    const columns = getColumns();
    if (!target || !columns) return;

    const menuOpened = target.classList.contains("k-grid-header-menu");
    const menuItemSelected = target.classList.contains("k-columnmenu-item");

    const fieldIsSortable =
      columns.find((c) => c.field === event?.event?.field)?.sortable && true;

    if (menuOpened) return;

    if (menuItemSelected && !fieldIsSortable) {
      const sortDescriptor = event?.event?.sort as SortDescriptor[];
      sortDescriptor?.filter(
        (s: SortDescriptor) => s.field !== event?.event?.field,
      );
      return;
    }
    if (typeof customHandler === "function") {
      nextTick(() => {
        if (activeFilterButton.value) activeFilterButton.value.focus();
        customHandler(event);
        // Kendo recreates header <th> nodes after each sort, which discards any
        // previously attached keydown listeners. Re-running setupHeaderThTriggers
        // here (after the sort data has been applied) reattaches those listeners
        // to the new nodes so subsequent Enter/Space presses continue to work.
        nextTick(() => {
          setupHeaderThTriggers();
        });
      });
    }
  };

  /**
   * Initialize accessibility (a11y) features for the grid.
   *
   * This function is idempotent — it will perform initialization only once.
   * On the first call it sets the internal `a11yInitialized` flag and runs
   * the following setup routines:
   *  - setupGridStyling()
   *  - setupTrNavigation()
   *  - setupFilterButtonAccessibility()
   *
   * Subsequent calls do nothing.
   *
   * Use this during component setup/mount to ensure grid accessibility helpers
   * are registered exactly once.
   *
   * @returns void
   */
  const initA11y = () => {
    if (a11yInitialized) return;
    try {
      setupGridStyling();
      setupTrNavigation();
      setupFilterButtonAccessibility();
      a11yInitialized = true;
    } catch (err) {
      console.error("initA11y failed:", err);
      // Clean up any partial listeners/observers so a retry can succeed.
      cleanupGridA11y();
    }
  };

  onBeforeUnmount(() => {
    cleanupGridA11y();
  });
  return {
    activeFilterButton,
    handleGridKeyDown,
    handleSortChange,
    initA11y,
  };
};
